import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, X, Calendar, Clock, MapPin } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ListingRequests({ credentials, onApprove }) {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/listing-requests`, {
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });
      const data = await response.json();
      setListings(data);
    } catch (error) {
      console.error('Error fetching listings:', error);
      toast.error('Failed to load listing requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (listing) => {
    // Update status to approved
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/listing-requests/${listing._id}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        toast.success('Listing approved - now create the event');
        fetchListings();
        
        // Open event form with pre-filled data via parent callback
        if (onApprove) {
          onApprove(listing);
        }
      } else {
        toast.error('Failed to approve listing');
      }
    } catch (error) {
      console.error('Error approving listing:', error);
      toast.error('An error occurred');
    }
  };

  const handleCreateEventFromListing = (listing) => {
    // For already approved listings, just open the event form
    if (onApprove) {
      onApprove(listing);
    }
  };

  const handleStatusChange = async (listingId, newStatus) => {
    try {
      const endpoint = newStatus === 'approved' 
        ? `${BACKEND_URL}/api/admin/listing-requests/${listingId}/approve`
        : `${BACKEND_URL}/api/admin/listing-requests/${listingId}/reject`;
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        toast.success(`Listing ${newStatus}`);
        fetchListings();
      } else {
        toast.error(`Failed to ${newStatus} listing`);
      }
    } catch (error) {
      console.error(`Error changing status:`, error);
      toast.error('An error occurred');
    }
  };

  const handleReject = async (listingId) => {
    if (!confirm('Are you sure you want to reject this listing?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/listing-requests/${listingId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        toast.success('Listing rejected');
        fetchListings();
      } else {
        toast.error('Failed to reject listing');
      }
    } catch (error) {
      console.error('Error rejecting listing:', error);
      toast.error('An error occurred');
    }
  };

  const formatDateTime = (listing) => {
    try {
      const date = new Date(listing.eventDate);
      const dateStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
      return `${dateStr}, ${listing.startTime} - ${listing.endTime}`;
    } catch {
      return 'Date not set';
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading listing requests...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Listing Requests</h2>
        <Badge variant="secondary">{listings.filter(l => l.status === 'pending').length} Pending</Badge>
      </div>

      {listings.length > 0 ? (
        <div className="max-h-[calc(100vh-250px)] overflow-y-auto scrollbar-admin pr-2">
          <div className="grid grid-cols-1 gap-4">
          {listings.map((listing) => (
            <Card key={listing._id} className={`${listing.status !== 'pending' ? 'opacity-60' : ''}`}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2">{listing.title}</CardTitle>
                    <Badge variant={listing.status === 'pending' ? 'default' : 'secondary'} className="capitalize">
                      {listing.status}
                    </Badge>
                  </div>
                  <div className="flex space-x-2">
                    {listing.status === 'pending' ? (
                      <>
                        <Button size="sm" onClick={() => handleApprove(listing)} className="bg-green-600 hover:bg-green-700">
                          <Check className="h-4 w-4 mr-1" />
                          Approve & Create Event
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleReject(listing._id)}>
                          <X className="h-4 w-4 mr-1" />
                          Reject
                        </Button>
                      </>
                    ) : (
                      <>
                        {listing.status === 'approved' ? (
                          <Button size="sm" variant="outline" onClick={() => handleStatusChange(listing._id, 'rejected')}>
                            Change to Rejected
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => handleStatusChange(listing._id, 'approved')}>
                            Change to Approved
                          </Button>
                        )}
                        <Button size="sm" onClick={() => handleCreateEventFromListing(listing)} className="bg-blue-600 hover:bg-blue-700">
                          Create Event from This
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">{listing.description}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    <span>{listing.location}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4 text-primary" />
                    <span>{formatDateTime(listing)}</span>
                  </div>
                  <div>
                    <span className="font-medium">Category:</span> {listing.category}
                  </div>
                  <div>
                    <span className="font-medium">Type:</span> {listing.type === 'invite-only' ? 'Invite Only' : 'Open'}
                  </div>
                  <div>
                    <span className="font-medium">Price:</span> {listing.price}
                  </div>
                  <div>
                    <span className="font-medium">Registration:</span> {listing.requiresRegistration ? 'Required' : 'Not Required'}
                  </div>
                </div>

                <div className="pt-3 border-t border-border">
                  <p className="text-xs font-medium text-muted-foreground mb-1">Submitted by:</p>
                  <div className="text-sm space-y-1">
                    <div><span className="font-medium">Name:</span> {listing.submitterName}</div>
                    <div><span className="font-medium">Email:</span> {listing.submitterEmail}</div>
                    {listing.submitterPhone && (
                      <div><span className="font-medium">Phone:</span> {listing.submitterPhone}</div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          </div>
        </div>
      ) : (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">No listing requests yet.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
